import React, { Component, createRef } from 'react';
import { RosterItem } from '../../../../../app/domain/Roster/RosterItem';
import { stringStartsWith } from '../../../../../app/utility/stringStartsWith';
import SelectInputMenu from './SelectInputMenu';

// Styles
import styles from './SelectInputMenu.module.css';

interface Props {
  columnCfg: any;
  onChange: (item: RosterItem, cfg: any) => void;
  onClick?: (e: React.MouseEvent) => void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  rosterItem: RosterItem;
}

export default class SelectInputColumn extends Component<Props> {
  public state = {
    menuCollapsed: true,
    userIsEditing: false,
    userOption: undefined as any,
    userValue: undefined as any,
    selected: '' as string
  };

  private fieldRef = createRef<HTMLTableCellElement>();
  private inputRef = createRef<HTMLInputElement>();

  constructor(props: Props) {
    super(props);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  private handleClickOutside(event: MouseEvent) {
    if (this.inputRef.current && !this.inputRef.current.contains(event.target as Node)) {
      setTimeout(() => {
        this.props.onBlur?.();
        this.onInputBlur();
      }, 250);
    }
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, true);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
  }

  public render() {
    const { columnCfg, rosterItem, onChange, onFocus, onBlur } = this.props;
    const { menuCollapsed, userOption } = this.state;
    const value = this.getValue();
    const options = columnCfg.editor.options as Array<{ value: string; text: string }>;
    const haveOptions = options && options.length > 0;
    const showMenu = haveOptions && !menuCollapsed;
    const errorMessage = rosterItem.errorMessage || {};

    return (
      <td className={columnCfg.id} ref={this.fieldRef} style={{ position: 'relative' }}>
        <input
          className={errorMessage[columnCfg.id.toLowerCase()]?.length > 0 ? styles.border : ''}
          autoComplete="offeveninchrome"
          onChange={(e) => this.onInputChange(e)}
          onClick={haveOptions ? () => this.onInputClick() : undefined}
          onFocus={onFocus}
          onBlur={() => {
            onBlur?.();
            this.onInputBlur();
          }}
          onKeyDown={(e) => this.onInputKeyDown(e)}
          readOnly={!haveOptions}
          ref={this.inputRef}
          type="text"
          value={value}
        />
        {showMenu && (
          <SelectInputMenu
            onItemSelect={(itemValue) => {
              this.onMenuSelect(itemValue);
              this.updateSelectedValue(itemValue);
            }}
            options={options}
            selectedOption={userOption}
          />
        )}
      </td>
    );
  }

  private getValue(): string {
    const { userIsEditing, userValue } = this.state;
    return userIsEditing && userValue !== undefined
      ? userValue
      : (this.props.rosterItem[this.props.columnCfg.field] as string);
  }

  private onInputClick() {
    const value = this.props.rosterItem[this.props.columnCfg.field] as string;
    this.startUserEdit(value);
  }

  private onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.startUserEdit(e.target.value);
  }

  private onInputBlur() {
    setTimeout(() => {
      this.cancelUserEdit();
    }, 250);
  }

  private onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const key = e.keyCode;
    if (key === 9 || key === 13) {
      this.onEnterKey();
    }
  }

  private onEnterKey() {
    const option = this.state.userOption;
    if (option) {
      this.onMenuSelect(option.value);
    } else {
      this.cancelUserEdit();
    }
    this.inputRef.current?.blur();
  }

  private updateSelectedValue(value: string) {
    const item = this.props.rosterItem;
    const columnCfg = this.props.columnCfg;
    item[columnCfg.field] = value;
    this.setState({ selected: value });
  }

  private startUserEdit(value: string) {
    const userOption = this.findOption(value);
    this.setState({
      menuCollapsed: false,
      userIsEditing: true,
      userOption,
      userValue: value,
      selected: value
    });
  }

  private findOption(value: string) {
    const options = this.props.columnCfg.editor.options as Array<{ value: string; text: string }>;
    return options.find((item) =>
      value.length > 0 ? stringStartsWith(item.text, value) : item.text.trim() === value.trim()
    );
  }

  private cancelUserEdit() {
    this.setState({
      menuCollapsed: true,
      userIsEditing: false,
      userOption: undefined,
      userValue: undefined,
      selected: ''
    });
  }

  private onMenuSelect(value: string) {
    const { rosterItem, columnCfg, onChange } = this.props;
    rosterItem[columnCfg.field] = value;
    onChange(rosterItem, columnCfg);
    this.cancelUserEdit();
  }
}
